import React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Code as CodeIcon, Layers as OperationsIcon } from '@mui/icons-material';
import { Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';

import {
	CodeStoreFileModel,
	ContextScopeModel,
	DeepOperationModel,
	OperationExecutionTypeModel,
	OperationExecutionTypesModel,
	OperationExposureModel,
	SchemaSummaryModel,
	SimplePropertyModel,
} from '../../../api/api';
import { ArtifactCategory, ArtifactPanel } from '../../../common/components/ArtifactPanel';
import McpIcon from '../../../common/components/McpIcon';
import Spinner from '../../../common/components/Spinner';
import { useSearchParamsEx } from '../../../common/hooks/useSearchParamsEx';
import PromptChatPanel from '../../../components/Chat/PromptChatPanel';
import ConfirmDialog from '../../../components/ConfirmDialog';
import ContextCodeFileItem from '../../ContextDesigner/Components/ContextCodeFileItem';
import { getContextArtifactId, parseContextArtifactId } from '../../ContextDesigner/ContextArtifactSelection';
import { useContextArtifactLayout } from '../../ContextDesigner/Hooks/useContextArtifactLayout';
import { OperationExposureChannel, participatesInApi, participatesInMcp } from '../Utils/operationExposure';
import CreateOperationDialog from './CreateOperationDialog';
import OperationItem from './OperationItem';

interface Props {
	embedded?: boolean;
	wrapMainContentInPaper?: boolean;
	operations: DeepOperationModel[];
	executionTypes: OperationExecutionTypeModel[];
	availableEntities: SchemaSummaryModel[];
	applicationCodeFiles?: CodeStoreFileModel[];
	testCodeFiles?: CodeStoreFileModel[];
	applicationCodeBaseId?: string;
	testCodeBaseId?: string;
	contextId: string;
	onAddProperty: (schemaId: string, property: SimplePropertyModel) => Promise<void> | void;
	onDeleteProperty: (entityId: string, propertyName: string) => Promise<void> | void;
	onDeleteCodeFile?: (fileName: string, codeBaseId: string) => Promise<void> | void;
	onRefreshCodeFile?: (fileName: string) => Promise<void> | void;
	onCreate: (
		name: string,
		description: string,
		executionType: OperationExecutionTypesModel,
		exposure: OperationExposureModel,
		selectedEntityIds: string[],
	) => Promise<string | void>;
	onUpdateOperation?: (operationId: string, name: string, executionType: OperationExecutionTypesModel) => Promise<void>;
	onUpdateExposure?: (operationId: string, exposure: OperationExposureModel) => Promise<void>;
	onUpdateDescription?: (operationId: string, description: string) => Promise<void>;
	onOperationCreated?: (operationId: string) => void;
	availableScopes: ContextScopeModel[];
	onScopesUpdated: () => void;
	onRefresh?: (operationId: string) => void | Promise<void>;
	onDeleteOperation?: (operationId: string) => Promise<void>;
	isLoading?: boolean;
}

type CodeFileDeleteTarget = {
	fileName: string;
	codeBaseId: string;
};

const OperationList: React.FC<Props> = ({
	embedded,
	wrapMainContentInPaper = true,
	operations,
	executionTypes,
	onCreate,
	onAddProperty,
	onDeleteProperty,
	availableEntities,
	applicationCodeFiles,
	testCodeFiles,
	applicationCodeBaseId,
	testCodeBaseId,
	contextId,
	availableScopes,
	onScopesUpdated,
	onOperationCreated,
	onRefresh,
	onRefreshCodeFile,
	onDeleteCodeFile,
	onDeleteOperation,
	onUpdateOperation,
	onUpdateExposure,
	onUpdateDescription,
	isLoading,
}) => {
	const [searchParams, setSearchParam] = useSearchParamsEx();
	const selectedItemId = searchParams.get('sid') ?? null;
	const setSelectedItemId = useCallback((itemId: string | null) => setSearchParam('sid', itemId ?? undefined), [setSearchParam]);

	const [isCreatingOperation, setIsCreatingOperation] = useState(false);
	const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ open: boolean; operationId: string | null; operationName: string }>({
		open: false,
		operationId: null,
		operationName: '',
	});
	const [codeFileDeleteTarget, setCodeFileDeleteTarget] = useState<CodeFileDeleteTarget | null>(null);
	const [exposureFilters, setExposureFilters] = useState<OperationExposureChannel[]>(['api', 'mcp']);
	const codeFiles = useMemo(() => applicationCodeFiles ?? [], [applicationCodeFiles]);
	const testsFiles = useMemo(() => testCodeFiles ?? [], [testCodeFiles]);
	const { createFolder, renameFolder, deleteFolder, moveArtifact, getFolderId, foldersByCategory } = useContextArtifactLayout(contextId);
	const filteredOperations = useMemo(
		() =>
			operations.filter((op) => {
				const showApi = exposureFilters.includes('api') && participatesInApi(op.exposure);
				const showMcp = exposureFilters.includes('mcp') && participatesInMcp(op.exposure);
				return showApi || showMcp;
			}),
		[exposureFilters, operations],
	);

	// Auto-select first operation if none is selected
	useEffect(() => {
		if (!embedded && !selectedItemId && operations.length > 0) {
			setSelectedItemId(getContextArtifactId({ kind: 'operation', id: operations[0].id }));
		}
	}, [embedded, selectedItemId, operations, setSelectedItemId]);

	const getHandlerFileNameForOp = useCallback((opName: string): string => `${opName.replace(/-/g, '')}Handler.cs`, []);

	// helper to get the handler file for a given op name
	const getHandlerForOp = useCallback(
		(opName: string): CodeStoreFileModel | null => {
			const target = getHandlerFileNameForOp(opName);
			const lowerTarget = target.toLowerCase();
			return codeFiles.find((file) => file.name.toLowerCase() === lowerTarget) ?? null;
		},
		[codeFiles, getHandlerFileNameForOp],
	);

	// Get selected operation
	const selection = parseContextArtifactId(selectedItemId);
	const selectedOperation = selection?.kind === 'operation' ? operations.find((operation) => operation.id === selection.id) : null;

	// Get selected code file
	const selectedCodeFileId = selection && ['application-file', 'test-file', 'domain-file'].includes(selection.kind) ? selection.id : undefined;
	const selectedCodeFile = selectedCodeFileId ? [...codeFiles, ...testsFiles].find((file) => file.id === selectedCodeFileId) : null;
	const selectedCodeBaseId = selectedCodeFile && testsFiles.some((file) => file.id === selectedCodeFile.id) ? testCodeBaseId : applicationCodeBaseId;

	// Build artifact categories
	const categories: ArtifactCategory[] = [
		{
			id: 'operations',
			label: 'Operations',
			icon: <OperationsIcon fontSize="small" />,
			items: filteredOperations.map((op) => ({
				id: getContextArtifactId({ kind: 'operation', id: op.id }),
				label: op.name,
				folderId: getFolderId('Operation', op.id),
				endAdornment: (
					<Box sx={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', ml: 1, width: 48 }}>
						<Box sx={{ display: 'inline-flex', justifyContent: 'center', width: 18 }}>
							{participatesInMcp(op.exposure) && (
								<Tooltip title="Exposed through MCP">
									<Box component="span" sx={{ display: 'inline-flex', color: 'primary.main' }}>
										<McpIcon size={14} />
									</Box>
								</Tooltip>
							)}
						</Box>
						<Box sx={{ display: 'inline-flex', justifyContent: 'flex-end', width: 30 }}>
							{participatesInApi(op.exposure) && (
								<Tooltip title="Exposed through API">
									<Box
										component="span"
										sx={{
											border: '1px solid',
											borderColor: 'primary.main',
											borderRadius: 0.75,
											color: 'primary.main',
											fontSize: 10,
											fontWeight: 700,
											lineHeight: '14px',
											px: 0.5,
										}}
									>
										API
									</Box>
								</Tooltip>
							)}
						</Box>
					</Box>
				),
			})),
			onAddItem: () => {
				// Show create operation UI
				setIsCreatingOperation(true);
			},
			addItemLabel: 'Add Operation',
			folders: foldersByCategory.operations,
			onCreateFolder: (name) => createFolder('operations', name),
			onRenameFolder: renameFolder,
			onDeleteFolder: deleteFolder,
			onMoveItemToFolder: (itemId, folderId) => moveArtifact('Operation', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
		},
		{
			id: 'codeview',
			label: 'Implementation',
			icon: <CodeIcon fontSize="small" />,
			items: codeFiles.map((file) => ({
				id: getContextArtifactId({ kind: 'application-file', id: file.id }),
				label: file.name,
				folderId: getFolderId('ApplicationCodeFile', file.id),
				onDelete:
					onDeleteCodeFile && applicationCodeBaseId ? () => setCodeFileDeleteTarget({ fileName: file.name, codeBaseId: applicationCodeBaseId }) : undefined,
			})),
			folders: foldersByCategory.codeview,
			onCreateFolder: (name) => createFolder('codeview', name),
			onRenameFolder: renameFolder,
			onDeleteFolder: deleteFolder,
			onMoveItemToFolder: (itemId, folderId) => moveArtifact('ApplicationCodeFile', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
		},
		{
			id: 'tests',
			label: 'Tests',
			icon: <CodeIcon fontSize="small" />,
			items: testsFiles.map((file) => ({
				id: getContextArtifactId({ kind: 'test-file', id: file.id }),
				label: file.name,
				folderId: getFolderId('TestCodeFile', file.id),
				onDelete: onDeleteCodeFile && testCodeBaseId ? () => setCodeFileDeleteTarget({ fileName: file.name, codeBaseId: testCodeBaseId }) : undefined,
			})),
			emptyLabel: 'No tests generated yet',
			folders: foldersByCategory.tests,
			onCreateFolder: (name) => createFolder('tests', name),
			onRenameFolder: renameFolder,
			onDeleteFolder: deleteFolder,
			onMoveItemToFolder: (itemId, folderId) => moveArtifact('TestCodeFile', parseContextArtifactId(itemId)?.id ?? itemId, folderId),
		},
	];

	// Check if Code View is selected (when a file is selected)
	const isCodeViewSelected = Boolean(selectedCodeFileId);

	const handleCreateOperation = async (
		name: string,
		description: string,
		executionType: OperationExecutionTypesModel,
		exposure: OperationExposureModel,
		selectedEntityIds: string[],
	) => {
		const result = await onCreate(name, description, executionType, exposure, selectedEntityIds);
		setIsCreatingOperation(false);

		// Select the newly created operation if we got its ID back
		if (result && typeof result === 'string') {
			setSelectedItemId(getContextArtifactId({ kind: 'operation', id: result }));
			onOperationCreated?.(result);
		}
	};

	const handleDeleteOperation = (operationId: string, operationName: string) => {
		setDeleteConfirmDialog({
			open: true,
			operationId,
			operationName,
		});
	};

	const handleConfirmDelete = async () => {
		if (deleteConfirmDialog.operationId) {
			if (onDeleteOperation) {
				try {
					await onDeleteOperation(deleteConfirmDialog.operationId);
					// Deselect if the deleted operation was selected
					if (selectedItemId === getContextArtifactId({ kind: 'operation', id: deleteConfirmDialog.operationId })) {
						setSelectedItemId(null);
					}
				} catch (error) {
					console.error('Failed to delete operation:', error);
				}
			} else {
				// Fallback if no delete handler provided
				if (selectedItemId === getContextArtifactId({ kind: 'operation', id: deleteConfirmDialog.operationId })) {
					setSelectedItemId(null);
				}
				console.log(`Deleting operation: ${deleteConfirmDialog.operationId}`);
			}
		}
		setDeleteConfirmDialog({ open: false, operationId: null, operationName: '' });
	};

	const handleCancelDelete = () => {
		setDeleteConfirmDialog({ open: false, operationId: null, operationName: '' });
	};

	const handleChatComplete = useCallback(async () => {
		if (selectedOperation) {
			await onRefresh?.(selectedOperation.id);
			await onRefreshCodeFile?.(getHandlerForOp(selectedOperation.name)?.name ?? getHandlerFileNameForOp(selectedOperation.name));
			return;
		}

		if (selectedCodeFile) {
			await onRefreshCodeFile?.(selectedCodeFile.name);
		}
	}, [getHandlerFileNameForOp, getHandlerForOp, onRefresh, onRefreshCodeFile, selectedCodeFile, selectedOperation]);

	const handleDeleteSelectedCodeFile = useCallback(
		async (fileName: string) => {
			if (!onDeleteCodeFile || !selectedCodeBaseId) {
				return;
			}

			setCodeFileDeleteTarget({ fileName, codeBaseId: selectedCodeBaseId });
		},
		[onDeleteCodeFile, selectedCodeBaseId],
	);

	const handleConfirmDeleteCodeFile = useCallback(async () => {
		if (!onDeleteCodeFile || !codeFileDeleteTarget) {
			return;
		}

		try {
			await onDeleteCodeFile(codeFileDeleteTarget.fileName, codeFileDeleteTarget.codeBaseId);
			if (selectedCodeFile?.name === codeFileDeleteTarget.fileName) {
				setSelectedItemId(null);
			}
		} finally {
			setCodeFileDeleteTarget(null);
		}
	}, [codeFileDeleteTarget, onDeleteCodeFile, selectedCodeFile?.name, setSelectedItemId]);

	return (
		<Stack
			direction="row"
			sx={{
				flexGrow: 1,
				minHeight: 0,
				overflow: 'hidden',
				gap: (theme) => `${theme.layout.panelGap}px`,
				px: (theme) => `${theme.layout.edgeGap}px`,
				py: (theme) => `${theme.layout.edgeGap}px`,
			}}
		>
			{/* Artifact Panel on the left */}
			{!embedded && (
				<ArtifactPanel
					title="Operations"
					titleIcon={<OperationsIcon />}
					categories={categories}
					selectedItemId={selectedItemId}
					onSelectItem={setSelectedItemId}
					searchPlaceholder="Search operations & files…"
					searchActions={
						<ToggleButtonGroup
							size="small"
							value={exposureFilters}
							aria-label="Operation exposure filters"
							onChange={(_, nextFilters: OperationExposureChannel[]) => {
								setExposureFilters(nextFilters);
							}}
							sx={{
								flex: '0 0 auto',
								'& .MuiToggleButton-root': {
									minWidth: 40,
									minHeight: 32,
									px: 1,
									py: 0.25,
								},
								'& .MuiToggleButton-root.Mui-selected': {
									bgcolor: 'primary.main',
									borderColor: 'primary.main',
									color: 'primary.contrastText',
									'&:hover': {
										bgcolor: 'primary.dark',
									},
								},
							}}
						>
							<ToggleButton value="api">API</ToggleButton>
							<ToggleButton value="mcp">MCP</ToggleButton>
						</ToggleButtonGroup>
					}
					defaultWidth={300}
					storageKey="context-designer-panel-width"
				/>
			)}

			{/* Main content area */}
			<Stack direction="row" sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
				<CreateOperationDialog
					executionTypes={executionTypes}
					onCreate={handleCreateOperation}
					availableEntities={availableEntities}
					open={isCreatingOperation}
					onOpenChange={(open) => setIsCreatingOperation(open)}
				/>

				{/* Content area */}
				{wrapMainContentInPaper ? (
					<Paper sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
						{isLoading && <Spinner />}
						{selectedOperation ? (
							// Operation details view
							<OperationItem
								contextId={contextId}
								operationHandlerCode={getHandlerForOp(selectedOperation.name)}
								executionTypes={executionTypes}
								onDeleteProperty={onDeleteProperty}
								onAddProperty={onAddProperty}
								operation={selectedOperation}
								availableScopes={availableScopes}
								onScopesUpdated={onScopesUpdated}
								onRefresh={onRefresh}
								onRefreshCodeFile={onRefreshCodeFile}
								onUpdateOperation={onUpdateOperation}
								onUpdateExposure={onUpdateExposure}
								onUpdateDescription={onUpdateDescription}
								onDelete={() => handleDeleteOperation(selectedOperation.id, selectedOperation.name)}
							/>
						) : isCodeViewSelected ? (
							// Code view - display selected file
							selectedCodeFile ? (
								<Stack direction="column" flex={1} overflow="auto">
									<ContextCodeFileItem
										file={selectedCodeFile}
										alwaysExpanded
										onRefresh={onRefreshCodeFile}
										onDelete={onDeleteCodeFile && selectedCodeBaseId ? handleDeleteSelectedCodeFile : undefined}
									/>
								</Stack>
							) : (
								<Typography color="textSecondary">Code file not found</Typography>
							)
						) : (
							// Empty state
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'textSecondary' }}>
								<Typography>Select an operation or view to get started</Typography>
							</Box>
						)}
					</Paper>
				) : (
					<Box sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
						{isLoading && <Spinner />}
						{selectedOperation ? (
							<OperationItem
								contextId={contextId}
								operationHandlerCode={getHandlerForOp(selectedOperation.name)}
								executionTypes={executionTypes}
								onDeleteProperty={onDeleteProperty}
								onAddProperty={onAddProperty}
								operation={selectedOperation}
								availableScopes={availableScopes}
								onScopesUpdated={onScopesUpdated}
								onRefresh={onRefresh}
								onRefreshCodeFile={onRefreshCodeFile}
								onUpdateOperation={onUpdateOperation}
								onUpdateExposure={onUpdateExposure}
								onUpdateDescription={onUpdateDescription}
								onDelete={() => handleDeleteOperation(selectedOperation.id, selectedOperation.name)}
							/>
						) : isCodeViewSelected ? (
							selectedCodeFile ? (
								<Stack direction="column" flex={1} overflow="auto">
									<ContextCodeFileItem
										file={selectedCodeFile}
										alwaysExpanded
										onRefresh={onRefreshCodeFile}
										onDelete={onDeleteCodeFile && selectedCodeBaseId ? handleDeleteSelectedCodeFile : undefined}
									/>
								</Stack>
							) : (
								<Typography color="textSecondary">Code file not found</Typography>
							)
						) : (
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'textSecondary' }}>
								<Typography>Select an operation or view to get started</Typography>
							</Box>
						)}
					</Box>
				)}

				{!embedded && (
					<PromptChatPanel
						contextId={contextId}
						targetType={selectedOperation ? 'Operation' : undefined}
						targetId={selectedOperation?.id}
						onComplete={handleChatComplete}
						onAssistantMessageReceived={handleChatComplete}
					/>
				)}
			</Stack>

			{/* Delete Confirmation Dialog */}
			<ConfirmDialog
				open={deleteConfirmDialog.open}
				title="Delete Operation"
				message={`Are you sure you want to delete the operation "${deleteConfirmDialog.operationName}"? This action cannot be undone.`}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<ConfirmDialog
				open={!!codeFileDeleteTarget}
				title="Delete Code File"
				message={`Delete "${codeFileDeleteTarget?.fileName ?? ''}"? This action cannot be undone.`}
				confirmText="Delete"
				confirmColor="error"
				onConfirm={handleConfirmDeleteCodeFile}
				onCancel={() => setCodeFileDeleteTarget(null)}
			/>
		</Stack>
	);
};

export default OperationList;
